import React, { useRef, useState, useEffect } from "react";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useDeviceType from "../../../custom-hooks/DeviceType";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import { plusIcon, rightDirectionBlackIcon, searchIcon } from "../../../assets";
import Button from "../../../component-lib/Molecules/button/Button";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import AddRuleDialog from "../dialogs/AddRuleDialog";
import EventEHRConfirmationDialog from "../../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import TooltipForTable from "../../../component-lib/Molecules/tooltip/TooltipForTable";
import {
  getCommunicationRules,
  getCommunicationRuleById,
  deleteCommunicationRule,
} from "../SettingsSaga";
import {
  setCommunicationRulesList,
  setCloseAddCommunicationRuleDialog,
} from "../SettingsSlice";
import { DISPOSITION_OPTIONS } from "./constants";

// Renders a list of values as chips (up to `max`, then a "+N" chip), with a
// tooltip that lists every record on its own line.
const ChipList = ({ items, max = 2 }: { items: string[]; max?: number }) => {
  if (!items.length) return <p className="text-neutral-90 font-normal">-</p>;
  const chip = (label: string, key: React.Key) => (
    <span
      key={key}
      className="inline-block bg-[#F1F1F1] text-[#565656] rounded-full px-2 py-[2px] text-[12px] whitespace-nowrap"
    >
      {label}
    </span>
  );
  const visible = items.slice(0, max);
  const remaining = items.length - visible.length;
  return (
    <Tooltip
      arrow
      placement="bottom-start"
      title={
        <div
          style={{ whiteSpace: "pre-line", fontSize: "12px", lineHeight: 1.5 }}
        >
          {items.join("\n")}
        </div>
      }
    >
      <div className="flex items-center gap-1">
        {visible.map((l, i) => chip(l, i))}
        {remaining > 0 && chip(`+${remaining}`, "more")}
      </div>
    </Tooltip>
  );
};

const CommunicationRuleScreen = () => {
  const navigate = useNavigate();
  const deviceType = useDeviceType();
  const dispatch = useDispatch();

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [pageNumber, setPageNumber] = useState(1);
  const [searchedValue, setSearchValue] = useState<string>("");

  // Dialog state
  const [dialogState, setDialogState] = useState<null | "add" | "edit">(null);
  const [defaultRuleData, setDefaultRuleData] = useState<any>({});

  // Confirmation dialog state
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [selectedRuleName, setSelectedRuleName] = useState<string>("");

  // Redux state
  const communicationRulesList = useSelector(
    (state: any) => state?.SETTINGS_SLICE?.communicationRulesList,
  );
  const loading = useSelector((state: any) => state?.LOADER_SLICE?.isLoading);
  const closeAddCommunicationRuleDialog = useSelector(
    (state: any) => state?.SETTINGS_SLICE?.closeAddCommunicationRuleDialog,
  );

  // Extract data from Redux state
  const notificationRules = communicationRulesList?.communicationRules || [];
  const totalCount = communicationRulesList?.total || 0;
  const totalPages = communicationRulesList?.totalPages || 0;

  // Fetch communication rules using Redux
  const fetchCommunicationRules = () => {
    dispatch(
      getCommunicationRules({
        pageNumber: pageNumber,
        limit: parseInt(rowPerPage || "10"),
        name: searchedValue || "",
      }),
    );
  };

  // Load data on component mount and when dependencies change
  useEffect(() => {
    fetchCommunicationRules();
  }, [pageNumber, rowPerPage, searchedValue]);

  // Handle Redux dialog close state for refresh
  useEffect(() => {
    if (closeAddCommunicationRuleDialog) {
      // Refresh data after successful operation (create, update, delete)
      fetchCommunicationRules();
      // Reset Redux state
      dispatch(setCloseAddCommunicationRuleDialog(false));
    }
  }, [closeAddCommunicationRuleDialog, dispatch]);

  // Note: Removed problematic useEffect that was causing data to disappear when opening edit dialog
  // Data refresh is now handled only by the Redux dialog close state useEffect above

  // COMMUNICATION RULE COLUMN DATA
  const CommunicationRuleColumns = [
    {
      field: "name",
      label: <div>Rule Name</div>,
      renderLogic: (row: any) => {
        const ruleName = row?.name || "Unnamed Rule";
        const formattedName =
          ruleName.charAt(0).toUpperCase() + ruleName.slice(1).toLowerCase();
        return (
          <p className="font-medium text-primary-main common-text-ellipse-style">
            {formattedName}
          </p>
        );
      },
    },
    {
      field: "disposition",
      label: <div>Disposition</div>,
      renderLogic: (row: any) => {
        const label = (row?.dispositions || [])
          .map(
            (d: string) =>
              DISPOSITION_OPTIONS.find((o) => o.value === d)?.label || d,
          )
          .join(", ");
        return <TooltipForTable label={label || "-"} />;
      },
    },
    {
      field: "facilities",
      label: <div>Facilities</div>,
      renderLogic: (row: any) => {
        const names = (row?.facilities || [])
          .map((f: any) => f?.name)
          .filter(Boolean);
        return (
          <ChipList items={names.length ? names : ["All Facilities"]} max={1} />
        );
      },
    },
    {
      field: "recipients",
      label: <div>Recipients</div>,
      renderLogic: (row: any) => {
        const names = (row?.recipients || [])
          .map((r: any) => {
            if (r?.recipientType === "directory") {
              return r?.contactDirectory?.name;
            }
            const u = r?.user || r?.adminUser;
            return u
              ? `${u?.firstName || ""} ${u?.lastName || ""}`.trim()
              : null;
          })
          .filter(Boolean);

        return <ChipList items={names} max={1} />;
      },
    },
    {
      field: "channels",
      label: <div>Channel</div>,
      renderLogic: (row: any) => {
        const isEmail =
          (row?.recipients || []).some((r: any) => r?.isEmail) || row?.isEmail;
        const isSMS =
          (row?.recipients || []).some((r: any) => r?.isSMS) || row?.isSMS;
        const channels: string[] = [];
        if (isEmail) channels.push("Email");
        if (isSMS) channels.push("SMS");

        return (
          <TooltipForTable
            label={channels.length > 0 ? channels.join(", ") : "-"}
          />
        );
      },
    },
    {
      field: "startDate",
      label: <div>Start Date</div>,
      renderLogic: (row: any) => {
        const startDate = row?.startDate
          ? new Date(row?.startDate)?.toLocaleDateString()
          : "N/A";
        return <p className="text-neutral-90 font-normal">{startDate}</p>;
      },
    },
    {
      field: "endDate",
      label: <div>End Date</div>,
      renderLogic: (row: any) => {
        const endDate = row?.endDate
          ? new Date(row?.endDate)?.toLocaleDateString()
          : "N/A";
        return <p className="text-neutral-90 font-normal">{endDate}</p>;
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        return (
          <>
            <div className="flex items-center">
              <MenuDropdown
                options={[
                  {
                    label: "Edit Rule",
                    onClickCb: () => {
                      handleEditRule(row);
                    },
                  },
                  {
                    label: "Delete Rule",
                    onClickCb: () => {
                      handleDeleteRule(row?.id, row?.name || "Unnamed Rule");
                    },
                  },
                ]}
              />
            </div>
          </>
        );
      },
    },
  ];

  // HANDLE SEARCH CHANGE VALUE AND ADDED SOME DELAY
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);

    const value = e?.target?.value;

    // CLEAR THE PREVIOUS TIMEOUT
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // SET A NEW TIMEOUT TO DELAY THE CALLBACK
    typingTimeoutRef.current = setTimeout(() => {
      setSearchValue(value?.trim());
    }, 500); // ADJUST DELAY AS NEEDED
  };

  const handleDeleteRule = (ruleId: string, ruleName: string) => {
    // Set selected rule data and show confirmation dialog
    setSelectedRuleId(ruleId);
    setSelectedRuleName(ruleName);
    setOpenConfirmationDialog(true);
  };

  const handleAddRule = () => {
    setDefaultRuleData({});
    setDialogState("add");
  };

  const handleEditRule = (rule: any) => {
    // Set the rule data and open edit dialog
    setDefaultRuleData(rule);
    setDialogState("edit");
  };

  const handleDialogClose = () => {
    setDialogState(null);
    setDefaultRuleData({});
  };

  const handleDialogSubmit = (data: any) => {
    setDialogState(null);
    setDefaultRuleData({});
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className={`flex ${deviceType === "mobile" ? "flex-col items-start gap-3" : "items-center"} justify-between w-full`}
        >
          <div className="flex items-center gap-3">
            <img
              className="cursor-pointer"
              src={rightDirectionBlackIcon}
              alt="back-arrow"
              style={{ height: "12.5px" }}
              onClick={() => {
                navigate("/settings");
              }}
            />
            <Heading
              title="Communication Rule"
              customStyle={{ color: "#1D1D1D" }}
            />
          </div>

          <div
            className={`flex ${deviceType === "mobile" ? "w-full justify-between" : ""} gap-3`}
          >
            <div style={{ maxWidth: "350px" }}>
              <SearchSelectDropdown
                freeSolo={true}
                searchedValue={searchedValue}
                onSearchValueChangeCb={(e) => handleChange(e)}
                size="small"
                label="Search by rule name"
                fullWidth={deviceType === "mobile" ? "160px" : "320px"}
                startIcon={
                  <img
                    alt="search-icon"
                    src={searchIcon}
                    width={"18px"}
                    height={"18px"}
                  />
                }
              />
            </div>
            <Button
              startIcon={
                <img
                  src={plusIcon}
                  alt={"add"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label="Add Rule"
              variant="contained"
              onClickCb={handleAddRule}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "120px",
              }}
            />
          </div>
        </div>
        <div style={{ overflow: "auto" }}>
          <Table
            columns={CommunicationRuleColumns}
            rows={notificationRules}
            loading={loading}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: totalPages,
              totalCount: totalCount,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page || 1);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage || "10");
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>

      {/* ADD/EDIT RULE DIALOG */}
      {dialogState && (
        <AddRuleDialog
          isOpen={!!dialogState}
          isEdit={dialogState === "edit"}
          defaultRuleData={defaultRuleData}
          handleClose={handleDialogClose}
          handleData={handleDialogSubmit}
        />
      )}

      {/* DELETE CONFIRMATION DIALOG */}
      {openConfirmationDialog && (
        <EventEHRConfirmationDialog
          // alertTitle={`Delete ${selectedRuleName}`}
          alertTitle={`Delete Communication Rule`}
          alertDescription={`Are you sure you want to delete the rule "${selectedRuleName}"? This action cannot be undone.`}
          cancelButtonTitle="Cancel"
          submitButtonTitle="Delete"
          open={openConfirmationDialog}
          handleClose={(action) => {
            if (action === "close") {
              setOpenConfirmationDialog(false);
              setSelectedRuleId(null);
              setSelectedRuleName("");
            } else if (action === "submit") {
              if (selectedRuleId) {
                // Delete communication rule using Redux
                dispatch(
                  deleteCommunicationRule({
                    ruleId: selectedRuleId,
                    comesFrom: "communication-rule-screen",
                  }),
                );
                setOpenConfirmationDialog(false);
                setSelectedRuleId(null);
                setSelectedRuleName("");
              }
            }
          }}
        />
      )}
    </WrapperCard>
  );
};

export default CommunicationRuleScreen;
